import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Filter, ArrowLeft, Grid, List } from 'lucide-react';
import { SlotSearchProvider, useSlotSearch } from './SlotSearchContext';
import SlotCard from './SlotCard';
import SlotFilterPanel from './SlotFilterPanel';
import MobileSlotFilters from './MobileSlotFilters';
import SlotDetailModal from './SlotDetailModal';
import Pagination from './Pagination';
import { Slot, Fair } from '../types';
import { slotApi, fairApi } from '../services/api';
import { showInfo } from '../utils/toast';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

function SlotSearchContent() {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();
  const [fair, setFair] = useState<Fair | null>(null);
  const [loadingFair, setLoadingFair] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [error, setError] = useState<string | null>(null);

  // Feature flag for booth comparison tool (Issue #1475)
  const { isEnabled: isBoothComparisonEnabled } = useFeatureFlag('booth_comparison');

  const {
    filters,
    results,
    totalResults,
    currentPage,
    totalPages,
    pageSize,
    setCurrentPage,
    setPageSize,
    isLoading,
    setIsLoading,
    setResults,
    selectedSlots,
    toggleSlotSelection,
    clearSelection,
    setSelectedFairId,
  } = useSlotSearch();

  // Parse and validate fairId
  const parsedFairId = fairId ? parseInt(fairId, 10) : null;
  const isValidFairId = parsedFairId !== null && !isNaN(parsedFairId) && parsedFairId > 0;

  // Load fair details
  useEffect(() => {
    let isMounted = true;
    const loadFair = async () => {
      if (!isValidFairId || !parsedFairId) {
        setError('Invalid fair ID');
        setLoadingFair(false);
        return;
      }
      try {
        setLoadingFair(true);
        const fairData = await fairApi.getById(parsedFairId);
        if (isMounted) {
          setFair(fairData);
          setSelectedFairId(parsedFairId);
        }
      } catch (err: unknown) {
        if (isMounted) {
          console.error('Failed to load fair:', err);
          setError('Failed to load fair details');
        }
      } finally {
        if (isMounted) {
          setLoadingFair(false);
        }
      }
    };
    loadFair();
    return () => { isMounted = false; };
  }, [isValidFairId, parsedFairId, setSelectedFairId]);

  // Load slots based on filters
  useEffect(() => {
    let isMounted = true;
    const loadSlots = async () => {
      if (!isValidFairId || !parsedFairId) return;
      try {
        setIsLoading(true);
        setError(null);
        const response = await slotApi.searchSlots(parsedFairId, {
          ...filters,
          page: currentPage,
          page_size: pageSize,
        });
        if (isMounted) {
          setResults(response);
        }
      } catch (err: unknown) {
        if (isMounted) {
          console.error('Failed to load slots:', err);
          setError('Failed to load slots. Please try again.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    loadSlots();
    return () => { isMounted = false; };
  }, [isValidFairId, parsedFairId, filters, currentPage, pageSize, setIsLoading, setResults]);

  const handleViewDetails = (slot: Slot) => {
    setSelectedSlot(slot);
    setDetailModalOpen(true);
  };

  const handleReserve = (slot: Slot) => {
    // Navigate to reservation page (to be implemented)
    navigate(`/fairs/${parsedFairId}/slots/${slot.id}/reserve`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate active filter count for mobile button
  const activeFilterCount = [
    filters.slot_type && filters.slot_type.length > 0,
    filters.has_power !== undefined,
    filters.min_price !== undefined || filters.max_price !== undefined,
    filters.is_available !== undefined && filters.is_available !== true,
    filters.search && filters.search.length > 0,
  ].filter(Boolean).length;

  if (loadingFair) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !fair) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Link to="/" className="text-secondary hover:text-primary-dark">
          Return to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to={`/fair/${fairId}`}
              className="flex items-center text-gray-600 hover:text-secondary transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-1" />
              Back to Fair
            </Link>
          </div>
          {fair && (
            <div className="mt-3">
              <h1 className="text-2xl font-bold text-gray-900">{fair.name}</h1>
              <p className="text-gray-600">
                Browse available vendor booth spaces for this event
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Filter Panel */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-4">
              <SlotFilterPanel />
            </div>
          </div>

          {/* Results Section */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="text-gray-600">
                    {isLoading ? 'Loading...' : `${totalResults} slot${totalResults !== 1 ? 's' : ''} found`}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  {/* View Mode Toggle */}
                  <div className="hidden sm:flex border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-teal-50 text-secondary' : 'text-gray-600 hover:bg-gray-50'}`}
                      title="Grid view"
                    >
                      <Grid className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-teal-50 text-secondary' : 'text-gray-600 hover:bg-gray-50'}`}
                      title="List view"
                    >
                      <List className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Mobile Filter Button */}
                  <button
                    onClick={() => setMobileFiltersOpen(true)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <Filter className="h-5 w-5" />
                    <span>Filters</span>
                    {activeFilterCount > 0 && (
                      <span className="inline-flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-primary rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Selected slots for comparison (Issue #1475: Gated by booth_comparison feature flag) */}
              {isBoothComparisonEnabled && selectedSlots.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    {selectedSlots.length} slot{selectedSlots.length !== 1 ? 's' : ''} selected for comparison
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={clearSelection}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => {
                        // See Issue #1304 - Comparison modal not yet implemented
                        showInfo('Comparison feature coming soon!');
                      }}
                      className="px-3 py-1 text-sm font-medium text-white bg-primary rounded hover:bg-primary-dark"
                    >
                      Compare
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {/* Loading State */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : results.length === 0 ? (
              /* Empty State */
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <p className="text-gray-600 mb-2">No slots found matching your criteria</p>
                <p className="text-sm text-gray-500">Try adjusting your filters</p>
              </div>
            ) : (
              /* Results Grid/List */
              <div
                className={
                  viewMode === 'grid'
                    ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4'
                    : 'space-y-4'
                }
              >
                {results.map((slot) => (
                  <SlotCard
                    key={slot.id}
                    slot={slot}
                    isSelected={selectedSlots.includes(slot.id)}
                    onSelect={toggleSlotSelection}
                    onViewDetails={handleViewDetails}
                    onReserve={handleReserve}
                    showCheckbox={isBoothComparisonEnabled}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalItems={totalResults}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemName="slots"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <MobileSlotFilters isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} />

      {/* Slot Detail Modal */}
      <SlotDetailModal
        slot={selectedSlot}
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        onReserve={handleReserve}
      />
    </div>
  );
}

export default function SlotSearchPage() {
  return (
    <SlotSearchProvider>
      <SlotSearchContent />
    </SlotSearchProvider>
  );
}
